import React, { useMemo, useEffect, useCallback } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { useForm, Controller, FormProvider } from "react-hook-form";
import { MInputTextField, MSelect, MCheckBox } from "src/M_Components";
import DynamicSelectBox from "./DynamicSelectBox"; // new FK select
import "./DynamicForm.css";

/* ===========================================================================================================================
 * DynamicForm
 *
 * High level contract and behavior:
 * - VIEW mode: the form always mirrors the latest `data`. When `data` or `mode` changes, we reset the form so the UI
 *   is a read only reflection of the entity.
 * - CREATE / EDIT modes: the form does NOT auto reset from `data` once mounted, so the user can type without losing input.
 *   On EDIT, we still allow a single hydration with fetched entity values if the form is pristine (empty).
 * - Imperative API via `formRef`: submit(), getValues(), reset(values, opts)
 * - Defaulting helpers only for CREATE: `defaultValuesOverride` and `getDefaultValue`.
 * - Emission of changes to the parent is lightweight: `onValuesChange` fires only on blur and on select/checkbox changes.
 *   For performance, we emit only the changed field, not the whole form, so parents avoid unnecessary work.
 *
 * Performance notes:
 * - We validate on blur to keep key press latency minimal.
 * - Each field is rendered inside a Controller, which isolates re renders to that field.
 * - We avoid calling onValuesChange on each keypress for text and number inputs.
 * - `schema` is filtered and sorted with useMemo to avoid repeated work.
 * - FK lists are no longer pre-fetched by the parent; FKs now use DynamicSelectBox with remote paging and per-tab query seeding.
 * ===========================================================================================================================
 */
export default function DynamicForm({
  mode = "view", // "view" | "create" | "edit"
  schema = [], // array of field option objects
  data = {}, // entity values to hydrate the form
  onSubmit = () => { }, // handler called on form submit
  extraSelects = {}, // dictionary of custom options arrays by field name
  includeInView = defaultIncludeInView, // predicate to include fields in VIEW mode
  includeInEdit = defaultIncludeInEdit, // predicate to include fields in EDIT mode
  includeInCreate = defaultIncludeInCreate, // predicate to include fields in CREATE mode
  title, // optional title shown inside the form
  containerClassName = "alignInputsUserForm", // legacy grid class
  align = "auto", // "auto" | "vertical"
  formRef = null, // external ref to expose imperative API
  defaultValuesOverride = {}, // CREATE only: basic default values to merge
  getDefaultValue = null, // CREATE only: per field default function
  onValuesChange = null, // callback(changedPatch, meta) fired on blur or select/checkbox change
  fkInitialQueries = {},
}) {
  const { t } = useTranslation();

  // Cheap booleans for mode checks.
  const isView = mode === "view";
  const isCreate = mode === "create";
  const isEdit = mode === "edit";

  // =========================================================================================================================
  // Compute initial default values
  // - For CREATE: merge (incoming data) + (defaultValuesOverride) + (getDefaultValue per field)
  // - For EDIT/VIEW: use incoming data as is
  //
  // This memo runs when create flag, data, schema, or defaulting helpers change. It is not expensive and runs on mount and
  // occasionally when inputs change identity. We avoid any deep work inside this memo.
  // =========================================================================================================================
  const initialDefaults = useMemo(() => {
    // Clone data so we never mutate props
    const base = { ...(data || {}) };

    if (isCreate) {
      // Start from base values, then override with explicit defaults
      const defaults = { ...base, ...defaultValuesOverride };

      // If a dynamic default function is provided, allow it to fill missing values
      if (typeof getDefaultValue === "function") {
        schema.forEach((opt) => {
          const val = getDefaultValue(opt, { mode, data });
          // Only apply when a value is not already present
          if (val !== undefined && defaults[opt.Name] === undefined) {
            defaults[opt.Name] = val;
          }
        });
      }
      return defaults;
    }

    // For edit/view we simply use server data
    return base;
  }, [isCreate, data, schema, defaultValuesOverride, getDefaultValue, mode]);

  // =========================================================================================================================
  // Initialize react-hook-form with our computed defaults
  // - mode: "onBlur" keeps typing snappy and only validates on blur
  // - reValidateMode: "onBlur" to keep behavior consistent after a value is set
  // - shouldFocusError: false to avoid focusing fields unexpectedly on submit
  // - shouldUnregister: false so fields keep values when conditionally unmounted (ex: tab layouts)
  // =========================================================================================================================
  const methods = useForm({
    defaultValues: initialDefaults,
    mode: "onBlur",
    reValidateMode: "onBlur",
    shouldFocusError: false,
    shouldUnregister: false,
  });

  // Destructure a few helpers we will use repeatedly
  const { control, handleSubmit, reset, getValues } = methods;

  // =========================================================================================================================
  // Keep data in sync for specific modes
  // - VIEW: always mirror new data, so read only form reflects latest entity
  // - EDIT: hydrate once if the form is still empty/pristine when data arrives
  //
  // Note: we check for "empty" by verifying that all current values are null or empty strings.
  // This is a common heuristic to avoid stomping on user input if they already started typing.
  // =========================================================================================================================
  useEffect(() => {
    if (isView) {
      // VIEW: full mirror of incoming data on any change
      reset(data || {});
    } else if (isEdit && data && Object.keys(data).length > 0) {
      // EDIT: hydrate only if form is still empty (pristine)
      const current = getValues();
      const isEmpty = Object.values(current).every((v) => v == null || v === "");
      if (isEmpty) reset(data);
    }
  }, [isView, isEdit, data, reset, getValues]);

  // =========================================================================================================================
  // Expose imperative API to parent through `formRef`
  // - submit: triggers handleSubmit pipeline
  // - getValues: returns current form values snapshot
  // - reset: allows parent to replace form values
  //
  // Important: we always replace the object on ref to reflect the latest closures.
  // =========================================================================================================================
  useEffect(() => {
    if (!formRef) return;
    formRef.current = {
      submit: () => handleSubmit(onSubmit)(),
      getValues: () => getValues(),
      reset: (vals, opts) => reset(vals ?? {}, opts),
    };
  }, [formRef, handleSubmit, onSubmit, reset, getValues]);

  // =========================================================================================================================
  // Change emission hook
  // - This function emits only the changed field and its value
  // - Called on blur of text/number/date and on change of select/checkbox
  // - Meta contains cause ("blur" | "change") and the field name
  //
  // This keeps parent reducers light, avoids re rendering loops, and scales well for large forms.
  // =========================================================================================================================
  const handleFieldChange = useCallback(
    (name, cause) => {
      if (typeof onValuesChange === "function") {
        // Read only the specific field value
        const val = getValues(name);
        // Emit a small patch containing only the changed field
        onValuesChange({ [name]: val }, { cause, name });
      }
    },
    [getValues, onValuesChange]
  );

  // =========================================================================================================================
  // Determine which fields to include based on mode
  // These predicates come from props and can be overridden by the parent.
  // Memo avoids recalculating the chosen predicate per render.
  // =========================================================================================================================
  const filterByMode = useMemo(() => {
    if (isCreate) return includeInCreate;
    if (isEdit) return includeInEdit;
    return includeInView;
  }, [isCreate, isEdit, includeInCreate, includeInEdit, includeInView]);

  // =========================================================================================================================
  // Compute the effective schema once per relevant changes
  // - Filter according to the chosen mode predicate
  // - Sort to keep UI stable (by Order then Name)
  // =========================================================================================================================
  const effectiveSchema = useMemo(() => {
    return asArray(schema).filter(filterByMode).sort(sortByOrderName);
  }, [schema, filterByMode]);

  //====================================================
  // Emit FK query changes so the parent can persist per tab/field.
  // We do not mutate form values here; we only provide meta for persistence.
  //====================================================
  const emitFkQueryChange = useCallback(
    (fieldName, q) => {
      if (typeof onValuesChange === "function") {
        onValuesChange({}, { cause: "fk-query", name: fieldName, query: q });
      }
    },
    [onValuesChange]
  );

  //====================================================
  // Emit FK options (current page) so the parent can use them (e.g., map pins aligned with dropdown results).
  // dont mutate form values here; we only provide meta for optional consumers.
  //====================================================
  const emitFkOptionsChange = useCallback(
    (fieldName, items) => {
      if (typeof onValuesChange === "function") {
        onValuesChange({}, { cause: "fk-options", name: fieldName, items });
      }
    },
    [onValuesChange]
  );

  // =========================================================================================================================
  // Container class computation
  // - if align="vertical", force vertical alignment class
  // - otherwise, use the provided containerClassName as is
  // =========================================================================================================================
  const containerClass = useMemo(() => {
    const forced = align === "vertical" ? "df-align-vertical" : "";
    return [containerClassName, forced].filter(Boolean).join(" ");
  }, [containerClassName, align]);

  // =========================================================================================================================
  // Field renderer
  // - This dispatcher creates one Controller per field, and chooses the correct component and coercion behavior
  // - Each field isolates re renders to itself thanks to RHF Controller
  // - make sure to keep conversion logic cheap and stable
  // =========================================================================================================================
  const renderField = (opt) => {
    const nameKey = opt?.Name || "";
    const label = opt?.NormalizedName || nameKey;

    // -----------------------------------------
    // Enums or custom selects (non FK)
    // Priority: extraSelects[name] if provided, otherwise map EnumType to options
    // -----------------------------------------
    if (
      extraSelects?.[nameKey] ||
      (Array.isArray(opt?.EnumType) && opt.EnumType.length > 0)
    ) {
      const extra = extraSelects?.[nameKey];
      const options =
        Array.isArray(extra) && extra.length
          ? extra
          : asArray(opt?.EnumType).map((e) => ({
            value: e?.Value ?? e?.Id ?? e,
            label: e?.Name ?? String(e),
          }));

      // Detect if values are numeric to coerce properly on change
      const optionsAreNumeric =
        Array.isArray(options) && options.length > 0
          ? typeof options[0].value === "number"
          : false;

      return (
        <Controller
          key={nameKey}
          name={nameKey}
          control={control}
          render={({ field }) => (
            <MSelect
              {...field}
              label={t(label)}
              options={options}
              // Map current scalar value to the option object
              value={
                options.find((o) => String(o.value) === String(field.value)) ||
                null
              }
              // Emit on change (selects are cheap and discrete)
              onChangeValue={(sel) => {
                field.onChange(
                  sel?.value == null
                    ? null
                    : optionsAreNumeric
                      ? Number(sel.value)
                      : sel.value
                );
                handleFieldChange(nameKey, "change");
              }}
              // Also emit on blur for completeness
              onBlur={() => handleFieldChange(nameKey, "blur")}
              disabled={isView}
              required={!!opt.IsRequired}
              autocomplete
            />
          )}
        />
      );
    }

    // -----------------------------------------
    // Foreign keys
    // - No more pre-fetched FK lists; use DynamicSelectBox with remote paging.
    // - In VIEW, show resolved label when server provided it (ex: data[Name]), fallback to numeric id.
    // - In EDIT/CREATE, pass tableName, groupBy, valueId, and initialQuery for tab-friendly UX.
    // -----------------------------------------
    if (opt?.IsForeignKey && opt?.ForeignTableName) {
      const fkFormKey = getFkFormKey(opt);
      const initialQuerySeed =
        // prefer a human-readable field from server payload (ex: "Currency" next to "CurrencyId")
        (data && data[nameKey] != null ? String(data[nameKey]) : "") || "";

      if (isView) {
        // VIEW mode: show server-resolved text when available; fallback to raw id
        const idVal = data?.[fkFormKey];
        const resolved =
          data && data[nameKey] != null
            ? String(data[nameKey])
            : idVal != null
              ? String(idVal)
              : "";
        return (
          <MInputTextField
            key={nameKey}
            label={t(opt?.NormalizedName || nameKey)}
            value={resolved}
            disabled
          />
        );
      }

      // EDIT/CREATE: render DynamicSelectBox and bind to RHF numeric id
      return (
        <Controller
          key={nameKey}
          name={fkFormKey}
          control={control}
          render={({ field }) => (
            <DynamicSelectBox
              label={t(opt?.NormalizedName || nameKey)}
              tableName={opt.ForeignTableName}
              groupBy={opt.GroupByInfo || "Id"}
              valueId={
                field.value == null ||
                  field.value === "" ||
                  Number(field.value) === 0
                  ? null
                  : Number(field.value)
              }
              required={!!opt.IsRequired}
              disabled={isView}
              initialQuery={
                isEdit || isCreate
                  ? fkInitialQueries?.[fkFormKey] ?? initialQuerySeed ?? ""
                  : ""
              }
              onQueryChange={(q) => emitFkQueryChange(fkFormKey, q)}
              onOptionsChange={(rows) => emitFkOptionsChange(fkFormKey, rows)}
              onChange={(sel) => {
                // store numeric id in the form
                const next = sel ? Number(sel.value) : null;
                field.onChange(next);
                handleFieldChange(fkFormKey, "change");
              }}
            />
          )}
        />
      );
    }

    // -----------------------------------------
    // Booleans
    // - emit on change for checkboxes (discrete)
    // - also emit on blur to keep behavior uniform
    // -----------------------------------------
    if (isBool(opt)) {
      return (
        <Controller
          key={nameKey}
          name={nameKey}
          control={control}
          render={({ field }) => (
            <MCheckBox
              label={t(label)}
              checked={Boolean(field.value)}
              onChange={(e) => {
                field.onChange(!!e?.target?.checked);
                handleFieldChange(nameKey, "change");
              }}
              onBlur={() => handleFieldChange(nameKey, "blur")}
              disabled={isView}
            />
          )}
        />
      );
    }

    // -----------------------------------------
    // Dates
    // - normalize values to yyyy-mm-dd for input type date
    // - emit ONLY on blur to avoid change storms while typing partial dates
    // -----------------------------------------
    if (isDateish(opt)) {
      return (
        <Controller
          key={nameKey}
          name={nameKey}
          control={control}
          render={({ field }) => (
            <MInputTextField
              label={t(label)}
              type="date"
              value={asInputDate(field.value)}
              onChange={(e) => {
                // Update local form state as the user types
                field.onChange(e?.target?.value ?? "");
                // We intentionally do not emit to parent here
              }}
              onBlur={() => handleFieldChange(nameKey, "blur")}
              required={!!opt.IsRequired}
              disabled={isView}
            />
          )}
        />
      );
    }

    // -----------------------------------------
    // Numbers
    // - keep the raw string while typing (so the user can type freely)
    // - emit ONLY on blur
    // -----------------------------------------
    if (isNumber(opt)) {
      return (
        <Controller
          key={nameKey}
          name={nameKey}
          control={control}
          render={({ field }) => (
            <MInputTextField
              label={t(label)}
              type="number"
              value={field.value ?? ""}
              onChange={(e) => {
                field.onChange(e?.target?.value ?? "");
                // No external emission here
              }}
              onBlur={() => handleFieldChange(nameKey, "blur")}
              required={!!opt.IsRequired}
              disabled={isView}
            />
          )}
        />
      );
    }

    // =========================================================================================================================
    // Strings
    // - keep the text responsive by not emitting on each keystroke
    // - emit ONLY on blur
    // =========================================================================================================================
    return (
      <Controller
        key={nameKey}
        name={nameKey}
        control={control}
        render={({ field }) => (
          <MInputTextField
            label={t(label)}
            value={field.value ?? ""}
            onChange={(e) => {
              field.onChange(e?.target?.value ?? "");
              // No external emission here
            }}
            onBlur={() => handleFieldChange(nameKey, "blur")}
            required={!!opt.IsRequired}
            disabled={isView}
          />
        )}
      />
    );
  };

  // =========================================================================================================================
  // Render
  // - wrap everything in FormProvider so children can use RHF hooks if needed
  // - render the title inside the legacy grid span
  // - map the effective schema to field renderers
  // =========================================================================================================================
  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onSubmit)} className="dynamic-form-root">
        <div className={containerClass}>
          {title && <span className="dynamic-form-title">{t(title)}</span>}

          {effectiveSchema.map((opt, idx) => (
            <div key={`df-fld-${opt?.Name || idx}`}>{renderField(opt)}</div>
          ))}
        </div>
      </form>
    </FormProvider>
  );
}

/* ===========================================================================================================================
 * Inclusion rules (defaults)
 * These predicates decide which fields appear in each mode.
 * Parents can override them per form if needed.
 * ===========================================================================================================================
 */

const defaultIncludeInCreate = (opt) => {
  if (!opt) return false;
  if (opt.IsKey || isAudit(opt.Name)) return false;
  if (opt.IsHiddenEdit === true) return false;
  if (opt.Name === "Inactive") return false;
  return true;
};

const defaultIncludeInEdit = (opt) => {
  if (!opt) return false;
  if (opt.IsKey || isAudit(opt.Name)) return false;
  if (opt.IsHiddenEdit === true) return false;
  return true;
};

const defaultIncludeInView = (opt) => {
  if (!opt) return false;
  if (opt.IsKey || isAudit(opt.Name)) return false;
  if (opt.IsHiddenList === true) return false;
  return true;
};

/* ===========================================================================================================================
 * Helpers
 * Utility functions to keep the render code small and focused.
 * ===========================================================================================================================
 */

// Detects audit columns to hide
const isAudit = (name) =>
  ["CreatedAt", "UpdatedAt", "CreatedBy", "UpdatedBy"].includes(name);

// Type guards from schema options
const isBool = (opt) => opt?.Type === "boolean" || opt?.Type === "bool";
const isNumber = (opt) =>
  ["int", "int32", "decimal", "double"].includes(opt?.Type);
const isDateish = (opt) =>
  ["date", "datetime", "Date", "DateTime"].includes(opt?.Type);

// Safe array casting
const asArray = (v) => (Array.isArray(v) ? v : []);

// Sort by Order then Name to keep stable layouts
const sortByOrderName = (a, b) => {
  const ao = a?.Order ?? 0;
  const bo = b?.Order ?? 0;
  if (ao !== bo) return ao - bo;
  return String(a?.Name || "").localeCompare(String(b?.Name || ""));
};

// Normalize various date inputs to yyyy-mm-dd for input type date
const asInputDate = (d) => {
  if (!d) return "";
  try {
    const s = String(d);
    if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10);
    const dd = new Date(s);
    if (!isNaN(dd.getTime())) return dd.toISOString().slice(0, 10);
  } catch { }
  return "";
};

// Consistent FK form key used by the form and payload logic
// If Header exists, use it. Otherwise, ensure Name ends with "Id"
const getFkFormKey = (opt) => {
  const nameKey = opt?.Name || "";
  if (opt?.Header) return opt.Header;
  return nameKey?.endsWith("Id") ? nameKey : `${nameKey}Id`;
};

// PropTypes for runtime safety and docs
DynamicForm.propTypes = {
  mode: PropTypes.oneOf(["create", "edit", "view"]),
  schema: PropTypes.array.isRequired,
  data: PropTypes.object,
  onSubmit: PropTypes.func,
  extraSelects: PropTypes.object,
  includeInView: PropTypes.func,
  includeInEdit: PropTypes.func,
  includeInCreate: PropTypes.func,
  title: PropTypes.string,
  containerClassName: PropTypes.string,
  align: PropTypes.oneOf(["auto", "vertical"]),
  formRef: PropTypes.any,
  defaultValuesOverride: PropTypes.object,
  getDefaultValue: PropTypes.func,
  onValuesChange: PropTypes.func,
  fkInitialQueries: PropTypes.object,
};
